import React, { useState, useEffect } from 'react';
import { Confetti } from '@phosphor-icons/react';

function calcTimeLeft(targetDate) {
  const now = new Date();
  const target = new Date(targetDate + 'T00:00:00');
  const diff = target - now;
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, passed: true };
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    passed: false,
  };
}

export function PartyCountdown({ title, date, emoji, color = '#FF6B35', compact = false, venue, time }) {
  const [timeLeft, setTimeLeft] = useState(() => calcTimeLeft(date));

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(calcTimeLeft(date)), 1000);
    return () => clearInterval(timer);
  }, [date]);

  const units = [
    { label: 'Days', value: timeLeft.days },
    { label: 'Hours', value: timeLeft.hours },
    { label: 'Min', value: timeLeft.minutes },
    { label: 'Sec', value: timeLeft.seconds },
  ];

  if (compact) {
    return (
      <div className="flex items-center gap-3" data-testid="party-countdown-compact">
        <span className="text-2xl">{emoji || '🎂'}</span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-white truncate">{title}</p>
          <div className="flex items-center gap-2 mt-1">
            {timeLeft.passed ? (
              <span className="text-xs font-bold" style={{ color }}>Today!</span>
            ) : (
              units.map(u => (
                <div key={u.label} className="text-center">
                  <span className="text-sm font-bold text-white">{String(u.value).padStart(2, '0')}</span>
                  <span className="text-[8px] text-neutral-500 ml-0.5">{u.label[0]}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="party-countdown" className="relative overflow-hidden">
      {/* Animated confetti dots */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(12)].map((_, i) => (
          <div key={i} className="absolute rounded-full animate-float"
            style={{
              width: 4 + (i % 3) * 2,
              height: 4 + (i % 3) * 2,
              background: [color, '#00E5FF', '#22C55E', '#F59E0B', '#A855F7', '#EC4899'][i % 6],
              opacity: 0.15 + (i % 4) * 0.05,
              left: `${(i * 8.3) % 100}%`,
              top: `${(i * 13.7) % 100}%`,
              animationDelay: `${i * 0.4}s`,
              animationDuration: `${3 + (i % 3)}s`,
            }} />
        ))}
      </div>

      {/* Header */}
      <div className="relative z-10 text-center mb-5">
        <span className="text-4xl block mb-2">{emoji || '🎂'}</span>
        <h3 className="text-sm sm:text-base font-bold text-white">{title}</h3>
        {venue && <p className="text-[10px] text-neutral-500 mt-1">{venue}{time ? ` | ${time}` : ''}</p>}
      </div>

      {/* Countdown Boxes */}
      <div className="relative z-10 flex items-center justify-center gap-2 sm:gap-3">
        {timeLeft.passed ? (
          <div className="text-center py-3">
            <Confetti size={32} weight="fill" style={{ color }} className="mx-auto mb-2" />
            <p className="text-lg font-bold" style={{ color }}>It's Party Time!</p>
          </div>
        ) : (
          units.map((u, i) => (
            <React.Fragment key={u.label}>
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center relative"
                  style={{
                    background: `${color}08`,
                    border: `1px solid ${color}20`,
                    boxShadow: `0 4px 20px ${color}10`,
                  }}>
                  <span className="text-xl sm:text-2xl font-bold text-white font-mono">
                    {String(u.value).padStart(2, '0')}
                  </span>
                </div>
                <span className="text-[9px] sm:text-[10px] text-neutral-500 font-semibold uppercase tracking-wider mt-1.5">{u.label}</span>
              </div>
              {i < units.length - 1 && (
                <div className="flex flex-col gap-1 pb-4">
                  <div className="w-1 h-1 rounded-full" style={{ background: color, opacity: 0.4 }} />
                  <div className="w-1 h-1 rounded-full" style={{ background: color, opacity: 0.4 }} />
                </div>
              )}
            </React.Fragment>
          ))
        )}
      </div>

      {/* Days text */}
      {!timeLeft.passed && timeLeft.days <= 7 && (
        <p className="relative z-10 text-center text-xs mt-4" style={{ color }}>
          {timeLeft.days === 0 ? 'Tomorrow is the big day!' : `Only ${timeLeft.days} day${timeLeft.days !== 1 ? 's' : ''} to go!`}
        </p>
      )}

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-12px) scale(1.2); }
        }
        .animate-float { animation: float 3s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
